import { useMemo } from 'preact/hooks'
import { FilterCollapse } from './FilterCollapse'
import { FilterInputCheckbox } from './FilterInputCheckbox'
import { FilterInputTabs } from './FilterInputTabs'
import { FilterInputSlider } from './FilterInputSlider'
import { FilterInputPalette } from './FilterInputPalette'
import { FilterInputSizing } from './FilterInputSizing'
import { FilterInputToggle } from './FilterInputToggle'
import {
  RANGE_INDICATOR,
  parseActiveRange,
  isSizeFilterField,
  isToggleFilterField,
  getToggleFilterValue,
  isToggleFilterActive,
  explodeFacetValues,
  parseFilterValueTerms,
  getRelatedFilterKeys,
  splitFilterPair
} from './FilterUtils'
import { getFilterDisplayCustomisations } from './FilterCustomisations'

export function FilterItem({ filter, activeFilters, fetchedFilters, onFilter, removeAll }) {
  if (!filter) return null

  const fetchedFilter = useMemo(() => {
    return fetchedFilters.find((current) => current.field === filter.field)
  }, [fetchedFilters])

  const filterCustomizations = useMemo(() => getFilterDisplayCustomisations(filter.field), [filter.field])

  const values = useMemo(() => {
    const exploded = explodeFacetValues(filter.field, filter.values)
    const fetchedExploded = explodeFacetValues(filter.field, fetchedFilter?.values)

    return exploded.map((item) => {
      if (item.type === 'range' || item.type === 'slider') {
        const value = `${item.low}${RANGE_INDICATOR}${item.high}`
        const valueWithKey = `${filter.field}:${value}`
        return {
          label: item.label ?? value,
          value: valueWithKey,
          count: item.count,
          checked: activeFilters.has(valueWithKey),
          disabled: false
        }
      }

      const label = item.label ?? item.value
      const valueWithKey = `${filter.field}:${item.value}`
      const relatedKeys = getRelatedFilterKeys(filter.field, item.value)
      const fetchedValue = fetchedExploded.find((currentValue) => {
        return String(currentValue.value).toLowerCase() === String(item.value).toLowerCase()
      })

      const customValue =
        filterCustomizations.values?.find((customItem) => {
          const customKey = customItem.value?.value ?? customItem.value
          const customTerms = parseFilterValueTerms(customKey)
          return (
            customKey === item.label ||
            customKey === item.value ||
            customTerms.some((term) => term.toLowerCase() === String(label).toLowerCase())
          )
        }) || {}

      const icon = customValue.icon || false
      const iconID = customValue.icon_id?.value ?? customValue.icon_id ?? false
      const aspectRatio = customValue.icon_aspect_ratio ?? false
      const iconWidth = customValue.icon_width ?? false
      const filterColour = customValue.filter_colour?.value ?? customValue.filter_colour ?? false

      const iconObj = icon
        ? { icon, aspectRatio: aspectRatio || 1, iconWidth }
        : iconID
          ? { iconID, aspectRatio: aspectRatio || 1, iconWidth }
          : false

      return {
        label,
        value: valueWithKey,
        count: fetchedValue?.count ?? item.count,
        checked: relatedKeys.some((key) => activeFilters.has(key)),
        disabled: Boolean(fetchedFilter) && !fetchedValue,
        color: filterColour,
        icon: iconObj
      }
    })
  }, [filter, activeFilters, fetchedFilter, filterCustomizations])

  if (filter.type !== 'slider' && !isToggleFilterField(filter.field) && !values.length) return null

  if (isToggleFilterField(filter.field)) {
    const checked = isToggleFilterActive(filter.field, activeFilters)

    return (
      <div className="flex flex-col border-b first:border-t border-neutrals-light-grey">
        <FilterInputToggle
          label={filter.label}
          checked={checked}
          onToggle={() => {
            if (checked) removeAll?.(filter.field)
            else onFilter(getToggleFilterValue(filter.field))
          }}
        />
      </div>
    )
  }

  const activeFilterLabel =
    filter.type === 'slider'
      ? parseActiveRange(filter.field, activeFilters)
        ? 1
        : 0
      : values.filter((item) => item.checked).length

  return (
    <FilterCollapse label={filter.label} activeFilterLabel={activeFilterLabel}>
      <div className="flex flex-col pb-6">
        {filter.type === 'slider' ? (
          <FilterInputSlider
            filter={filter}
            fetchedFilter={fetchedFilter}
            onFilter={onFilter}
            onRemove={() => removeAll?.(filter.field)}
          />
        ) : filter.type === 'palette' ? (
          <FilterInputPalette name={filter.field} values={values} onFilter={onFilter} />
        ) : filter.label === 'Gender' ? (
          <FilterInputTabs name={filter.field} values={values} onFilter={onFilter} removeAll={removeAll} />
        ) : (
          <FilterInputCheckbox name={filter.field} values={values} onFilter={onFilter} />
        )}
      </div>
    </FilterCollapse>
  )
}

export function FilterList({
  displayOrder,
  initialFilters,
  initialSizeFilters,
  fetchedFilters,
  sizeFilters,
  activeFilters,
  onFilter,
  removeAll
}) {
  return displayOrder.map((item) => {
    if (item.type === 'size') {
      if (!initialSizeFilters?.length) return null
      return (
        <FilterSizeItem
          key="size"
          initialFilters={initialSizeFilters}
          fetchedFilters={sizeFilters}
          activeFilters={activeFilters}
        />
      )
    }

    const filter = initialFilters.find((current) => current.field === item.field)
    if (!filter) return null

    return (
      <FilterItem
        key={filter.field}
        filter={filter}
        onFilter={onFilter}
        fetchedFilters={fetchedFilters}
        activeFilters={activeFilters}
        removeAll={removeAll}
      />
    )
  })
}

export function FilterSizeItem({ initialFilters, activeFilters, fetchedFilters }) {
  if (!initialFilters) return null

  const activeFilterLabel = [...(activeFilters || [])].filter((item) =>
    isSizeFilterField(splitFilterPair(item).field)
  ).length

  return (
    <FilterCollapse label="Size" activeFilterLabel={activeFilterLabel}>
      <div className="pb-6">
        <FilterInputSizing
          initialFilters={initialFilters}
          fetchedFilters={fetchedFilters}
          activeFilters={activeFilters}
        />
      </div>
    </FilterCollapse>
  )
}
